import styled from 'foundations-theming/utils';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { DragSource } from 'react-dnd-compat';
import I18n from 'I18n';
import PropTypes from 'prop-types';
import { CardSection } from 'foundations-components/transitional/components/Card';
import { CardWrapper } from 'foundations-components/transitional/components/Card';
import { CardHero } from 'foundations-components/transitional/components/CardHero';
import { Image } from 'foundations-components/transitional/components/Image';
import { TruncateMultiline } from 'foundations-components/transitional/components/TruncateMultiline';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { DRAG_TYPES } from 'ContentEditorUI/utils/DragAndDropConstants';
// @ts-expect-error ts-migrate(7016) FIXME: Could not find a declaration file for module 'Cont... Remove this comment to see the full error message
import { openDeleteSectionModal } from 'ContentEditorUI/redux/actions/inpageActions';
import { Illustration } from 'foundations-components/transitional/components/Illustration';
import { DescriptionList } from 'foundations-components/transitional/components/DescriptionList';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import FormattedDateTime from 'I18n/components/FormattedDateTime';
import InpageSectionTag from './InpageSectionTag';
import { getSidebarIframeWindow } from 'InpageEditorUI/lib/domUtils/sidebarIframeDomUtils';
import { isEmbed as isEmbedTypeGuard, isFixedLayoutSection, isSavedSection as isSavedSectionTypeGuard } from 'ContentEditorUI/types/Section';
import { View } from 'foundations-components/transitional/components/View';
import { Flex } from 'foundations-components/components/Flex';
import { WigglyAnimation } from '../add/WigglyAnimation';
import { Text } from 'foundations-components/components/Text';
import { DeleteIcon } from 'foundations-assets/icons/components/DeleteIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const DraggableSectionTypes = {
  MARKETER: 'marketer',
  DEVELOPER: 'developer',
  EMBED: 'embed'
};
const getDraggableSectionType = section => {
  if (isEmbedTypeGuard(section)) {
    return DraggableSectionTypes.EMBED;
  }
  if (isSavedSectionTypeGuard(section)) {
    return DraggableSectionTypes.MARKETER;
  }
  return DraggableSectionTypes.DEVELOPER;
};
const moduleSource = {
  beginDrag(props) {
    const dragPayload = {
      // Little bit of coupling here as embeds and custom sections both have `id`s
      customSectionId: props.section.id,
      sectionTree: props.section.sectionTree,
      type: 'customSection',
      isColumn: false,
      isRow: true,
      isTopMostRow: true,
      isEmbed: isEmbedTypeGuard(props.section),
      isMarketerSection: isSavedSectionTypeGuard(props.section),
      sectionName: props.section.name,
      sectionLabel: props.section.label,
      themePath: props.themePath
    };
    props.onDragStart(dragPayload);
    return dragPayload;
  },
  endDrag(props, monitor) {
    const dropResult = monitor.getDropResult();
    const dragItem = monitor.getItem();
    if (!monitor.didDrop()) {
      props.onDragEnd(undefined, dragItem);
      return;
    }
    props.onDragEnd(dropResult, dragItem);
  }
};
const Draggable = DragSource(DRAG_TYPES.CUSTOM_SECTION, moduleSource, monitor => ({
  isDragging: monitor.isDragging()
}));
const CardAnimationWrapper = styled(WigglyAnimation)(({
  theme
}) => {
  return {
    transition: 'all .3s ease-in-out !important',
    ':hover': {
      border: `${theme.borderWidth['100']} solid ${theme.color['fill-info-default']}`,
      boxShadow: theme.boxShadow['300'],
      transform: 'translateZ(10px)',
      borderRadius: theme.borderRadius['100']
    }
  };
});
const OpacityWrapper = styled.div.withConfig({
  displayName: "InpageDraggableSection__OpacityWrapper"
})([".hs-private.hs-private.hs-private &{transition:opacity 0.3s ease-in-out !important;opacity:0;}.hs-private.hs-private.hs-private ", ":hover &{opacity:1;}.hs-private.hs-private.hs-private ", ":active &{opacity:0;}"], CardAnimationWrapper, CardAnimationWrapper);
const DeleteSectionButton = ({
  sectionId
}) => {
  const sidebarIframeWindow = getSidebarIframeWindow();
  const dispatch = useDispatch();
  return /*#__PURE__*/_jsx(OpacityWrapper, {
    children: /*#__PURE__*/_jsx(IconButton, {
      use: "unstyled",
      tooltip: "Delete",
      onClick: () => {
        dispatch(openDeleteSectionModal(sectionId));
      },
      tooltipProps: {
        _bodyElement: sidebarIframeWindow.document.body
      },
      children: /*#__PURE__*/_jsx(DeleteIcon, {})
    })
  });
};
const Paragraph = styled.span.withConfig({
  displayName: "InpageDraggableSection__Paragraph"
})([".hs-private.hs-private.hs-private &{}"]);
const ListContainer = styled.div.withConfig({
  displayName: "InpageDraggableSection__ListContainer"
})(({
  theme
}) => {
  return {
    display: 'grid',
    gridAutoFlow: 'column',
    gridTemplateColumns: '5fr 5fr 4fr',
    gridGap: 'auto',
    marginBlockStart: theme.space['300']
  };
});
const MockImageContainer = styled.div.withConfig({
  displayName: "InpageDraggableSection__MockImageContainer"
})(({
  theme
}) => {
  return {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    backgroundColor: theme.color['fill-field-default'],
    paddingBlock: theme.space['100'],
    paddingInline: theme.space['1500']
  };
});
const DisabledSectionOverlay = styled.div.withConfig({
  displayName: "InpageDraggableSection__DisabledSectionOverlay"
})(({
  theme
}) => {
  return {
    position: 'absolute',
    insetBlockStart: 0,
    insetBlockEnd: 0,
    insetInlineStart: 0,
    insetInlineEnd: 0,
    backgroundColor: theme.color['text-core-disabled'],
    opacity: 0.5,
    borderRadius: theme.borderRadius['100']
  };
});
const EmbedContent = ({
  updatedByName,
  updatedAt,
  pageCount
}) => {
  return /*#__PURE__*/_jsx(DescriptionList, {
    children: /*#__PURE__*/_jsxs(ListContainer, {
      children: [/*#__PURE__*/_jsxs("div", {
        children: [/*#__PURE__*/_jsx("dt", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "addSectionPanel.customSections.embedDescriptionList.lastUpdatedBy"
          })
        }), /*#__PURE__*/_jsx("dd", {
          children: updatedByName
        })]
      }), /*#__PURE__*/_jsxs("div", {
        children: [/*#__PURE__*/_jsx("dt", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "addSectionPanel.customSections.embedDescriptionList.lastUpdatedOn"
          })
        }), /*#__PURE__*/_jsx("dd", {
          children: /*#__PURE__*/_jsx(FormattedDateTime, {
            value: updatedAt,
            format: "short-date"
          })
        })]
      }), /*#__PURE__*/_jsxs("div", {
        children: [/*#__PURE__*/_jsx("dt", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "addSectionPanel.customSections.embedDescriptionList.embeddedIn"
          })
        }), /*#__PURE__*/_jsx("dd", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "addSectionPanel.customSections.embedDescriptionList.assetCount",
            options: {
              count: pageCount
            }
          })
        })]
      })]
    })
  });
};
const InpageSection = ({
  section,
  sectionType,
  showTags = true
}) => {
  const [isWiggly, setIsWiggly] = useState(false);
  const toggleWiggleCard = () => {
    setIsWiggly(!isWiggly);
  };
  const imageAltText = section.description || I18n.text('addSectionPanel.sectionAltText');
  const isEmbed = isEmbedTypeGuard(section);
  const isMarketer = isSavedSectionTypeGuard(section);
  return /*#__PURE__*/_jsx(CardAnimationWrapper, {
    $isWiggly: isWiggly,
    $animationName: "clickSectionItem",
    onClick: toggleWiggleCard,
    onAnimationEnd: toggleWiggleCard,
    children: /*#__PURE__*/_jsxs(CardWrapper, {
      compact: true,
      marginBlockEnd: "0",
      children: [/*#__PURE__*/_jsx(CardHero, {
        children: /*#__PURE__*/_jsxs(View, {
          borderBlockEndWidth: "100",
          borderBlockEndStyle: "solid",
          borderBlockEndColor: "border-core-subtle",
          children: [!isEmbed && /*#__PURE__*/_jsx(Image, {
            alt: imageAltText,
            src: section.screenshotUrl
          }), isEmbed && /*#__PURE__*/_jsx(MockImageContainer, {
            children: /*#__PURE__*/_jsx(Illustration, {
              name: "mobile-onboarding-embed-tracking-code"
            })
          })]
        })
      }), /*#__PURE__*/_jsxs(CardSection, {
        paddingBlockStart: "0",
        marginBlockStart: "300",
        paddingBlockEnd: "300",
        children: [/*#__PURE__*/_jsxs(Flex, {
          alignItems: "center",
          marginBlockEnd: "200",
          justifyContent: "space-between",
          children: [/*#__PURE__*/_jsx(TruncateMultiline, {
            tagName: "strong",
            "data-test-id": `${sectionType}-section-label`,
            text: section.label || section.name,
            maxChars: section.isSmart ? 40 : 60,
            Paragraph: Paragraph
          }), isMarketer && /*#__PURE__*/_jsx(DeleteSectionButton, {
            sectionId: section.id
          })]
        }), showTags && /*#__PURE__*/_jsxs(Flex, {
          alignItems: "center",
          children: [isMarketer && section.isSmart && /*#__PURE__*/_jsx(InpageSectionTag, {
            title: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "addSectionPanel.customSections.smart"
            }),
            helpTitle: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "addSectionPanel.customSections.smartSectionInfo"
            })
          }), isFixedLayoutSection(section) && /*#__PURE__*/_jsx(InpageSectionTag, {
            title: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "addSectionPanel.customSections.fixedLayout"
            }),
            helpTitle: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "addSectionPanel.customSections.fixedLayoutLayoutInfo"
            })
          }), isEmbed && /*#__PURE__*/_jsx(InpageSectionTag, {
            title: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "addSectionPanel.customSections.embed"
            }),
            helpTitle: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "addSectionPanel.customSections.embedInfo"
            })
          })]
        }), isEmbed && /*#__PURE__*/_jsx(EmbedContent, {
          updatedAt: section.updatedAt,
          updatedByName: section.updatedByName,
          pageCount: section.pageCount
        }), !isEmbed && section.description && /*#__PURE__*/_jsx(Text, {
          marginBlockStart: "200",
          as: "p",
          children: section.description
        })]
      })]
    })
  });
};
function InpageDraggableSection({
  section,
  connectDragSource,
  isDraggable = true,
  notAddableMessage = /*#__PURE__*/_jsx(FormattedMessage, {
    message: "inpage.layout.addSection.genericNotAddable"
  }),
  showTags = true
}) {
  const sectionType = getDraggableSectionType(section);
  const sectionElement = /*#__PURE__*/_jsx("div", {
    "data-hs-dragdrop": true,
    "data-test-id": `${sectionType}-section-${section.label || section.name}`,
    style: {
      cursor: `${isDraggable ? 'grab' : 'not-allowed'}`
    },
    children: /*#__PURE__*/_jsx(InpageSection, {
      section: section,
      sectionType: sectionType,
      isDraggable: isDraggable,
      showTags: showTags
    })
  }, `section-${section.id}`);
  if (isDraggable) {
    return connectDragSource(sectionElement);
  }
  const sidebarIframeWindow = getSidebarIframeWindow();
  return /*#__PURE__*/_jsx(Tooltip, {
    _bodyElement: sidebarIframeWindow.document.body,
    use: "longform",
    title: notAddableMessage,
    children: /*#__PURE__*/_jsxs(View, {
      position: "relative",
      children: [sectionElement, /*#__PURE__*/_jsx(DisabledSectionOverlay, {})]
    })
  });
}

// @ts-expect-error The `Section` type doesn't play well with
// PropTypes.object.isRequired, since it seems that Draggable needs parity
// between the component property types and the PropTypes. I'm omitting updating
// this for now.
export default Draggable(InpageDraggableSection);
InpageDraggableSection.propTypes = {
  section: PropTypes.object.isRequired,
  connectDragSource: PropTypes.func.isRequired,
  isDraggable: PropTypes.bool,
  onDragStart: PropTypes.func.isRequired,
  onDragEnd: PropTypes.func.isRequired,
  showTags: PropTypes.bool
};